import type React from 'react';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import NewReportIcon from '@/assets/icons/new_report.svg?component';
import Plus from '@/assets/icons/plus.svg?component';
import { Button } from '@/components/atoms/Buttons';
import Checkbox from '@/components/atoms/Checkbox';
import FormField from '@/components/atoms/FormField';
import StyledSingleDatePicker from '@/components/atoms/StyledSingleDatePicker/StyledSingleDatePicker';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';
import TextField from '@/components/atoms/TextField';
import Tooltip from '@/components/atoms/Tooltip';
import { ModalBody, ModalComponent, ModalFooter } from '@/components/molecules/ModalComponent';
import type { NewReport } from '@/domain/harvestReports';

import { LabelWithHelp } from './components/LabelWithHelp';
import { WeightLimitFields } from './components/WeightLimitFields';
import { areWeightLimitsValid, type FormDataState, toNewReport } from './createHarvestReportForm';

import styles from './CreateHarvestReportModal.module.scss';

interface CreateHarvestReportModalProps {
    onSubmitted: (requestBody: NewReport) => Promise<void>;
}

export function CreateHarvestReportModal(props: CreateHarvestReportModalProps) {
    const { onSubmitted } = props;

    const { t } = useTranslation(['harvestReport', 'common']);
    const [open, setOpen] = useState(false);
    const modalId = useId();
    const lastFeedDayId = useId();
    const slaughterDayId = useId();

    const [formData, setFormData] = useState<FormDataState>({
        temperature: undefined,
        lastFeedDay: null,
        slaughterDay: null,
        weightMin: undefined,
        weightMax: undefined,
        isDraft: false,
    });
    function updateForm<T extends keyof FormDataState>(key: T) {
        return (value: FormDataState[T]) =>
            setFormData((formData) => ({ ...formData, [key]: value }));
    }

    const toggle = () => setOpen(!open);
    const hasValidWeightLimits = areWeightLimitsValid(formData.weightMin, formData.weightMax);
    const showWeightLimitsError =
        !hasValidWeightLimits && (formData.weightMin != null || formData.weightMax != null);
    const isValid = !!(formData.lastFeedDay && formData.slaughterDay && hasValidWeightLimits);
    const onFormDataonSubmit = (ev: React.FormEvent<HTMLFormElement>) => {
        ev.preventDefault();
        if (isValid) {
            onSubmitted(toNewReport(formData));
            setOpen(false);
        }
    };

    return (
        <>
            <Button
                id={modalId}
                className={styles.createReportButton}
                variant={'primary'}
                size={'medium'}
                data-testid={`${modalId}-openNewReportModal`}
                trackingId="harvest_report_create_open"
                onClick={() => setOpen(true)}
                icon={<SvgIcon color="currentColor" icon={Plus} size="custom" customSize={18} />}>
                {t('harvestReport:createHarvestReport.newReport')}
            </Button>
            <ModalComponent
                className={styles.modalDialog}
                isOpen={open}
                onClose={toggle}
                keyboard={true}
                centered={true}
                title={t('harvestReport:createHarvestReport.newReport')}
                titleIcon={NewReportIcon}
                closeTrackingId="harvest_report_create_close">
                <form onSubmit={onFormDataonSubmit}>
                    <ModalBody>
                        <FormField
                            label={
                                <LabelWithHelp help={t('harvestReport:common.lastFeedDayTooltip')}>
                                    {t('harvestReport:common.lastFeedDay')}
                                </LabelWithHelp>
                            }
                            htmlFor={lastFeedDayId}>
                            <StyledSingleDatePicker
                                date={formData.lastFeedDay}
                                required={true}
                                onDateChange={updateForm('lastFeedDay')}
                            />
                        </FormField>
                        <FormField
                            label={
                                <LabelWithHelp help={t('harvestReport:common.harvestDateTooltip')}>
                                    {t('harvestReport:common.harvestDate')}
                                </LabelWithHelp>
                            }
                            htmlFor={slaughterDayId}>
                            <StyledSingleDatePicker
                                date={formData.slaughterDay}
                                onDateChange={updateForm('slaughterDay')}
                            />
                        </FormField>
                        <FormField
                            label={
                                <LabelWithHelp
                                    help={t('harvestReport:common.seaTemperatureTooltip')}>
                                    {`${t('harvestReport:common.seaTemperature')} (${t('harvestReport:common.optional')})`}
                                </LabelWithHelp>
                            }
                            htmlFor={`${modalId}-temperature`}>
                            <TextField
                                id={`${modalId}-temperature`}
                                name="temperature"
                                type="number"
                                min="-20"
                                max="30"
                                step="0.1"
                                value={formData.temperature ?? ''}
                                onChange={(ev) => {
                                    const temperatureValue =
                                        ev.target.value === ''
                                            ? undefined
                                            : Number(ev.target.value);
                                    updateForm('temperature')(temperatureValue);
                                }}
                                placeholder={t('harvestReport:common.seaTemperature')}
                                required={false}
                            />
                        </FormField>
                        <WeightLimitFields
                            modalId={modalId}
                            weightMin={formData.weightMin}
                            weightMax={formData.weightMax}
                            showError={showWeightLimitsError}
                            onWeightMinChange={updateForm('weightMin')}
                            onWeightMaxChange={updateForm('weightMax')}
                        />
                        <div className={styles.checkboxContainer}>
                            <label className={styles.checkboxLabel} htmlFor={`${modalId}-draft`}>
                                <Checkbox
                                    id={`${modalId}-draft`}
                                    name="draft"
                                    checked={formData.isDraft ?? false}
                                    onCheckedChange={(checked) => {
                                        updateForm('isDraft')(checked);
                                    }}
                                />
                                <Tooltip
                                    placement="top"
                                    maxWidth={250}
                                    content={t('harvestReport:common.draftBadgeTooltip')}
                                    layout="inline">
                                    <span>{t('harvestReport:common.draft')}</span>
                                </Tooltip>
                            </label>
                        </div>
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            variant="secondary"
                            trackingId="harvest_report_create_cancel"
                            onClick={() => setOpen(false)}>
                            {t('common:actions.cancel')}
                        </Button>
                        <Button
                            variant="primary"
                            type="submit"
                            trackingId="harvest_report_create_submit"
                            disabled={!isValid}>
                            {t('harvestReport:createHarvestReport.createReport')}
                        </Button>
                    </ModalFooter>
                </form>
            </ModalComponent>
        </>
    );
}
